import {
  canWriteProperties,
  readCurrentAuthorization,
  type CurrentAuthorization,
} from '@/api/core/authorization';
import {
  readRoomOnlineAccess,
  validateRoomOnlineAccessWrite,
  writeRoomOnlineAccess,
  type OnlineAccessPermission,
  type RoomOnlineAccess,
  type RoomOnlineAccessReplacement,
  type RoomOnlineAccessRetirement,
} from '@/api/core/room-online-access';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useRef, useState } from 'react';
import type { PropertyRuntime } from '../property/property-queries';
import { readRoomAccessWithPermissionCheck } from './room-access-read';

interface Props {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  roomId: string;
  isCurrent: () => boolean;
  initialSnapshot?: RoomOnlineAccess | null;
  onSaved?: () => void;
}
interface Attempt {
  method: 'PUT' | 'DELETE';
  body: RoomOnlineAccessReplacement | RoomOnlineAccessRetirement;
}
const mayWrite = (value: CurrentAuthorization) =>
  value.assigned && (value.role_bits & (4 | 8)) !== 0 && canWriteProperties(value);
const equalItems = (a: OnlineAccessPermission[], b: OnlineAccessPermission[]) => {
  const keys = (items: OnlineAccessPermission[]) =>
    items.map((i) => `${i.kind}\0${i.target_namespace}\0${i.target_ref}`).sort();
  return JSON.stringify(keys(a)) === JSON.stringify(keys(b));
};

/** Remounting discards drafts/uncertain operations whenever the selected owner changes. */
export function RoomOnlineAccessSection(props: Props) {
  return (
    <RoomSettings
      key={`${props.runtime.apiNamespace}:${String(props.generation)}:${props.propertyId}:${props.roomId}`}
      {...props}
    />
  );
}

function RoomSettings({
  runtime,
  generation,
  propertyId,
  roomId,
  isCurrent,
  initialSnapshot,
  onSaved,
}: Props) {
  const session = useOperatorSession(runtime.session);
  const [snapshot, setSnapshot] = useState<RoomOnlineAccess | null | undefined>(initialSnapshot);
  const [items, setItems] = useState<OnlineAccessPermission[]>([]);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
  const errorId = useId();
  const editor = useRef<HTMLDivElement>(null);
  const [validation, setValidation] = useState<Attempt['method'] | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const rowErrors = new Map<number, string>();
  if (validation === 'PUT') {
    const seen = new Map<string, number>();
    items.forEach((item, index) => {
      try {
        validateRoomOnlineAccessWrite('PUT', {
          expected_revision: 0,
          reason: '검증',
          permission_items: [item],
        });
      } catch {
        rowErrors.set(
          index,
          item.target_namespace === 'LORA_HOTEL_ROOM'
            ? '객실 출입 권한의 호텔:객실 형식으로 입력해 주세요.'
            : '영문·숫자로 시작하는 160자 이하의 대상 참조를 입력해 주세요.',
        );
      }
      const key = JSON.stringify([item.kind, item.target_namespace, item.target_ref]);
      const previous = seen.get(key);
      if (previous !== undefined) {
        rowErrors.set(previous, '중복된 대상입니다. 다른 대상 참조를 입력해 주세요.');
        rowErrors.set(index, '중복된 대상입니다. 다른 대상 참조를 입력해 주세요.');
      }
      seen.set(key, index);
    });
  }
  const incompatibleRows = new Set(
    validation === 'PUT'
      ? items.flatMap((item, index) =>
          item.target_namespace === 'LORA_HOTEL_ROOM' && item.kind !== 'ROOM_ENTRY' ? [index] : [],
        )
      : [],
  );
  const reasonError =
    validation && (!reason.trim() || reason.trim().length > 240)
      ? '변경 사유를 1~240자로 입력해 주세요.'
      : '';
  useEffect(() => {
    if (focusRequest) editor.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [focusRequest]);
  const alive = useRef(true);
  const flight = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const currentSelection = useRef(isCurrent);
  currentSelection.current = isCurrent;
  const allowed = () => {
    const now = runtime.session.getSnapshot();
    return (
      alive.current &&
      currentSelection.current() &&
      now.status === 'authorized' &&
      now.generation === generation &&
      !now.deniedDomains.includes('property')
    );
  };
  const writable = () => {
    const authorization = runtime.session.getSnapshot().authorization;
    return allowed() && !!authorization && mayWrite(authorization);
  };
  const [invalidated, setInvalidated] = useState(false);
  useEffect(() => {
    alive.current = true;
    // Revocation permanently invalidates this mounted editor, including in-flight replay state.
    const unsubscribe = runtime.session.subscribe(() => {
      const now = runtime.session.getSnapshot();
      if (
        now.status !== 'authorized' ||
        now.generation !== generation ||
        now.deniedDomains.includes('property') ||
        !now.authorization ||
        !mayWrite(now.authorization)
      ) {
        alive.current = false;
        controller.current?.abort();
        setInvalidated(true);
        setAttempt(null);
        setReceipt('');
        setError('권한 또는 로그인 상태가 바뀌었습니다. 화면을 다시 열어 주세요.');
      }
    });
    return () => {
      alive.current = false;
      controller.current?.abort();
      flight.current = false;
      unsubscribe();
    };
  }, [runtime, generation]);
  const deny = () => {
    runtime.session.reportDomainAccessDenied('property', generation);
  };
  const load = async () => {
    if (!allowed() || flight.current) return;
    flight.current = true;
    setBusy(true);
    setError('');
    setReceipt('');
    const abort = new AbortController();
    controller.current = abort;
    try {
      const value = await readRoomAccessWithPermissionCheck(
        runtime,
        generation,
        propertyId,
        roomId,
        abort.signal,
        allowed,
      );
      if (!allowed() || abort.signal.aborted) return;
      setSnapshot(value);
      setItems(value?.permission_items ?? []);
      setReason('');
      setValidation(null);
      setEditing(false);
      setAttempt(null);
      setUncertain(false);
      setConflict(false);
    } catch (failure) {
      if (!allowed() || abort.signal.aborted) return;
      if (isApiError(failure) && failure.status === 403) {
        setSnapshot(undefined);
        setEditing(false);
        setAttempt(null);
        setUncertain(false);
        setError(
          '지점 조회 권한은 확인했지만 이 객실 출입 설정은 서버에서 거부했습니다. 다시 조회하거나 관리자에게 확인해 주세요.',
        );
      } else setError('출입 설정을 불러오지 못했습니다. 다시 조회해 주세요.');
    } finally {
      if (controller.current === abort) {
        flight.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  useEffect(() => {
    if (initialSnapshot === undefined)
      void load(); /* Selection identity is the keyed mount boundary. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const confirm = (method: Attempt['method']) => {
    if (!writable() || busy || snapshot === undefined || snapshot?.status === 'RETIRED') return;
    const body =
      method === 'PUT'
        ? {
            expected_revision: snapshot?.revision ?? 0,
            permission_items: items.map((item) => ({ ...item })),
            reason: reason.trim(),
          }
        : { expected_revision: snapshot?.revision ?? 0, reason: reason.trim() };
    try {
      validateRoomOnlineAccessWrite(method, body);
      setValidation(null);
      setUncertain(false);
      setAttempt({ method, body });
      setError('');
    } catch {
      setValidation(method);
      setFocusRequest((value) => value + 1);
      setError(
        '권한 항목과 변경 사유를 확인해 주세요. 중복 없이 최대 40개, 사유는 1~240자로 입력해 주세요.',
      );
    }
  };
  const save = async (command: Attempt) => {
    if (!writable() || flight.current || invalidated) return;
    flight.current = true;
    setBusy(true);
    setError('');
    setReceipt('');
    const abort = new AbortController();
    controller.current = abort;
    try {
      const authorization = await readCurrentAuthorization(runtime.client, abort.signal);
      if (!writable()) return;
      if (!mayWrite(authorization)) {
        deny();
        setError('설정을 변경할 권한이 없습니다.');
        return;
      }
      const result = await writeRoomOnlineAccess(
        runtime.client,
        propertyId,
        roomId,
        command.method,
        command.body,
        abort.signal,
      );
      if (!writable()) return;
      const readback = await readRoomOnlineAccess(runtime.client, propertyId, roomId, abort.signal);
      if (!writable()) return;
      if (
        readback?.binding_id !== result.binding_id ||
        readback.revision !== result.revision ||
        readback.status !== result.status ||
        readback.reason !== command.body.reason ||
        !equalItems(
          readback.permission_items,
          command.method === 'PUT'
            ? (command.body as RoomOnlineAccessReplacement).permission_items
            : [],
        )
      ) {
        setConflict(true);
        setAttempt(null);
        setError('저장 후 설정이 바뀌었거나 확인되지 않았습니다. 최신 설정을 다시 조회해 주세요.');
        return;
      }
      setSnapshot(readback);
      setItems(readback.permission_items);
      setEditing(false);
      setAttempt(null);
      setUncertain(false);
      onSaved?.();
      setReceipt(
        command.method === 'PUT'
          ? '설정을 저장하고 다시 확인했습니다.'
          : '설정을 종료하고 다시 확인했습니다.',
      );
    } catch (failure) {
      if (!allowed() || abort.signal.aborted) return;
      if (isApiError(failure) && (failure.status === 403 || failure.status === 401)) {
        setAttempt(null);
        setUncertain(false);
        if (failure.status === 403) deny();
        setError('설정을 변경할 권한 또는 로그인 상태를 확인해 주세요.');
      } else if (isApiError(failure) && failure.status === 409) {
        setAttempt(null);
        setConflict(true);
        setUncertain(false);
        setError('저장 정보가 바뀌었습니다. 최신 설정을 다시 조회한 뒤 편집해 주세요.');
      } else if (isApiError(failure) && failure.status === 400) {
        setAttempt(null);
        setError('입력한 설정을 확인해 주세요.');
      } else {
        setUncertain(true);
        setError(
          '저장 결과를 확인하지 못했습니다. 자동으로 다시 보내지 않았습니다. 같은 요청을 다시 보내거나 최신 설정을 조회해 주세요.',
        );
      }
    } finally {
      if (controller.current === abort) {
        flight.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  const canEdit =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    mayWrite(session.authorization) &&
    !invalidated;
  return (
    <section aria-label="온라인 출입 설정" className="mt-4 space-y-3 border-t pt-4">
      <h3 className="body-strong">
        {initialSnapshot === undefined ? '온라인 출입 설정' : '온라인 출입 설정 변경'}
      </h3>
      {initialSnapshot === undefined && (
        <p className="body text-muted-foreground">
          저장된 설정은 사용 가능 여부 미확인 상태입니다. 장비 적용이나 실제 출입 권한 부여를 뜻하지
          않습니다.
        </p>
      )}
      {initialSnapshot === undefined &&
        (snapshot === undefined ? (
          <p role="status">{busy ? '설정을 확인하고 있습니다.' : '출입 설정 미확인'}</p>
        ) : (
          <p>
            {snapshot === null
              ? '저장된 온라인 출입 설정이 없습니다.'
              : snapshot.status === 'RETIRED'
                ? '설정 종료됨'
                : `설정 저장됨 · 사용 가능 여부 미확인 · 항목 ${String(snapshot.permission_items.length)}개`}
          </p>
        ))}
      {receipt && <p role="status">{receipt}</p>}
      {error && <p role="alert">{error}</p>}
      <Button variant="outline" disabled={busy || invalidated} onClick={() => void load()}>
        {initialSnapshot === undefined ? '설정 다시 조회' : '편집용 최신 설정 조회'}
      </Button>
      {canEdit && snapshot !== undefined && snapshot?.status !== 'RETIRED' && !editing && (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            setItems(snapshot?.permission_items ?? []);
            setValidation(null);
            setEditing(true);
            setReceipt('');
          }}
        >
          설정 편집
        </Button>
      )}
      {editing && canEdit && !conflict && (
        <div ref={editor} className="space-y-3">
          <fieldset
            disabled={busy || !!attempt}
            className="space-y-3"
            onChange={() => {
              setError('');
            }}
          >
            <legend className="body-strong">저장할 전체 권한 항목</legend>
            {items.map((item, index) => (
              <div key={index} className="flex flex-wrap gap-2">
                <label>
                  권한 종류 {index + 1}
                  <Select
                    value={item.kind}
                    onValueChange={(value) => {
                      setError('');
                      if (value)
                        setItems(items.map((v, i) => (i === index ? { ...v, kind: value } : v)));
                    }}
                  >
                    <SelectTrigger aria-label={`권한 종류 ${String(index + 1)}`}>
                      <SelectValue>
                        {
                          {
                            ROOM_ENTRY: '객실 출입',
                            PROPERTY_ROUTE: '지점 경로',
                            ELEVATOR_FLOOR: '승강기 층',
                          }[item.kind]
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ROOM_ENTRY">객실 출입</SelectItem>
                      <SelectItem value="PROPERTY_ROUTE">지점 경로</SelectItem>
                      <SelectItem value="ELEVATOR_FLOOR">승강기 층</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label>
                  대상 구분 {index + 1}
                  <Select
                    value={item.target_namespace}
                    onValueChange={(value) => {
                      setError('');
                      if (value)
                        setItems(
                          items.map((v, i) =>
                            i === index
                              ? {
                                  ...v,
                                  target_namespace: value,
                                }
                              : v,
                          ),
                        );
                    }}
                  >
                    <SelectTrigger
                      aria-label={`대상 구분 ${String(index + 1)}`}
                      aria-invalid={incompatibleRows.has(index) || undefined}
                      aria-describedby={
                        incompatibleRows.has(index) ? `${errorId}-row-${String(index)}` : undefined
                      }
                    >
                      <SelectValue>
                        {
                          { CLUE_PLACE: 'Clue 장소', LORA_HOTEL_ROOM: 'LoRa 호텔·객실' }[
                            item.target_namespace
                          ]
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CLUE_PLACE">Clue 장소</SelectItem>
                      <SelectItem value="LORA_HOTEL_ROOM">LoRa 호텔·객실</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label>
                  대상 참조 {index + 1}
                  <Input
                    aria-label={`대상 참조 ${String(index + 1)}`}
                    aria-invalid={
                      (rowErrors.has(index) && !incompatibleRows.has(index)) || undefined
                    }
                    aria-describedby={
                      rowErrors.has(index) && !incompatibleRows.has(index)
                        ? `${errorId}-row-${String(index)}`
                        : undefined
                    }
                    maxLength={160}
                    value={item.target_ref}
                    onChange={(e) => {
                      setItems(
                        items.map((v, i) =>
                          i === index ? { ...v, target_ref: e.target.value } : v,
                        ),
                      );
                    }}
                  />
                  {rowErrors.has(index) && (
                    <p id={`${errorId}-row-${String(index)}`} className="caption text-destructive">
                      {rowErrors.get(index)}
                    </p>
                  )}
                </label>
                <Button
                  variant="outline"
                  onClick={() => {
                    setItems(items.filter((_, i) => i !== index));
                    setError('');
                  }}
                >
                  항목 {index + 1} 삭제
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              disabled={items.length >= 40}
              onClick={() => {
                setItems([
                  ...items,
                  { kind: 'ROOM_ENTRY', target_namespace: 'CLUE_PLACE', target_ref: '' },
                ]);
              }}
            >
              권한 항목 추가
            </Button>
            <p className="caption">
              확인된 대상 참조를 입력해 주세요. Clue는 영문·숫자·밑줄·하이픈, LoRa는 호텔:객실
              형식이며 객실 출입에만 사용할 수 있습니다.
            </p>
            <label className="block">
              변경 사유
              <Input
                aria-label="변경 사유"
                aria-invalid={!!reasonError || undefined}
                aria-describedby={reasonError ? `${errorId}-reason` : undefined}
                maxLength={240}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
              {reasonError && (
                <p id={`${errorId}-reason`} className="caption text-destructive">
                  {reasonError}
                </p>
              )}
            </label>
            <Button
              disabled={busy}
              onClick={() => {
                confirm('PUT');
              }}
            >
              변경 내용 확인
            </Button>
            {snapshot && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  confirm('DELETE');
                }}
              >
                설정 종료 확인
              </Button>
            )}
          </fieldset>
          {attempt && (
            <div role="group" aria-label="설정 변경 확인" className="space-y-2 rounded border p-3">
              <p>
                {attempt.method === 'DELETE'
                  ? '설정을 종료합니다. 종료 기록은 보존되며 다시 편집할 수 없습니다.'
                  : `전체 권한 항목 ${String(items.length)}개로 저장합니다.`}
              </p>
              <p className="body">변경 사유: {attempt.body.reason}</p>
              <Button disabled={busy} onClick={() => void save(attempt)}>
                {uncertain ? '같은 요청 다시 보내기' : '확인 후 저장'}
              </Button>
              {!uncertain && (
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setAttempt(null);
                  }}
                >
                  편집으로 돌아가기
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
